import { memo } from "react";
import BestStartButton from "../../../components/button";
import {
  IoBookmarkOutline,
  IoBusinessOutline,
  IoCalendarOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoChatbubbleOutline,
  IoCreateOutline,
  IoHelpCircleOutline,
  IoPersonOutline,
  IoRefresh,
  IoSaveOutline,
  IoTimeOutline,
  IoTrendingDownOutline,
  IoTrendingUpOutline,
  IoWarningOutline,
  IoClose 
} from "react-icons/io5";

function renderStatCard({ tone, icon, value, label }) {
  return (
    <div className={`home-stat-card ${tone}`}>
      <div className="home-stat-icon" aria-hidden="true">{icon}</div>
      <div className="home-stat-content">
        <div className="home-stat-value">{value}</div>
        <div className="home-stat-label">{label}</div>
      </div>
    </div>
  );
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function ScorecardTopbar({
  summaryTone,
  isLoading,
  isSaving,
  isDataRefreshing,
  isGoalSaving,
  isViewingHistory,
  draft,
  lastHydratedAt,
  totalFields,
  missingFields,
  bandTotals,
  budgetTotals,
  scorecardHistory,
  historyLoading,
  previousMonthLoading,
  onLoadCurrent,
  onRefresh,
  onTriggerDataRefresh,
  onSave,
  onCancel,
  isAdmin,
  isManager,
  managerViewMode,
  hasUnsavedChanges,
  onOpenFeedback,
}) {
  const hasOpenScorecard = Boolean(draft);
  const now = new Date();
  const previousMonthIndex = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
  const currentMonth = MONTH_NAMES[previousMonthIndex];
  const historyCount = scorecardHistory?.length ?? 0;
  const totalFocusNotes = (scorecardHistory ?? []).reduce((sum, item) => sum + (item.goalCount ?? 0), 0);
  const showSaveButton = Boolean(draft) && !isViewingHistory && hasUnsavedChanges;
  const showCancelButton = hasOpenScorecard;
  const showCreateButton = !hasOpenScorecard && (!isManager || managerViewMode === "my-scorecard");
  const showRefreshButton = hasOpenScorecard && !isViewingHistory;
  const feedbackCount = draft?.feedbacks?.length ?? 0;

  return (
    <section className={`home-topbar home-command-band ${summaryTone}`} aria-label="Scorecard creation summary">
      <div className="home-topbar-left">
        {!hasOpenScorecard && <h2 className="home-brand-title">KPI Scorecard Workspace</h2>}
        {hasOpenScorecard && (draft?.managerName || draft?.centreName) && (
          <div className="home-scorecard-meta" aria-label="Scorecard owner details">
            {draft?.managerName && (
              <span className="home-scorecard-meta-item">
                <IoPersonOutline className="home-scorecard-meta-icon" aria-hidden="true" />
                <span className="home-scorecard-meta-text">
                  <span className="home-scorecard-meta-label">Manager</span>
                  <span className="home-scorecard-meta-value">{draft.managerName}</span>
                </span>
              </span>
            )}
            {draft?.centreName && (
              <span className="home-scorecard-meta-item">
                <IoBusinessOutline className="home-scorecard-meta-icon" aria-hidden="true" />
                <span className="home-scorecard-meta-text">
                  <span className="home-scorecard-meta-label">Centre</span>
                  <span className="home-scorecard-meta-value">{draft.centreName}</span>
                </span>
              </span>
            )}
          </div>
        )}
        
        <p className="home-updated-text" aria-live="polite">
          <IoRefresh className={isLoading ? "spinning" : ""} aria-hidden="true" />
          {lastHydratedAt
            ? `Imported ${lastHydratedAt.toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}`
            : "No current scorecard loaded yet"}
        </p>
      </div>

      <div className="home-topbar-right">
        {hasOpenScorecard ? (
        <div className="home-stats-grid" aria-label="Scorecard overview">
          {renderStatCard({ tone: "success", icon: <IoCheckmarkCircleOutline />, value: bandTotals?.passed ?? 0, label: "Acheiving" })}
          {renderStatCard({ tone: "warning", icon: <IoWarningOutline />, value: bandTotals?.warning ?? 0, label: "Review" })}
          {renderStatCard({ tone: "error", icon: <IoCloseCircleOutline />, value: bandTotals?.failed ?? 0, label: "Needs attention" })}
          {renderStatCard({ tone: "missing", icon: <IoHelpCircleOutline />, value: missingFields ?? 0, label: "Missing" })}
        </div>
        ) : (
        <div className="home-stats-grid home-stats-grid--overview" aria-label="Workspace overview">
          {renderStatCard({ tone: "success", icon: <IoCalendarOutline />, value: currentMonth, label: "Current Month" })}
          {renderStatCard({ tone: "success", icon: <IoTimeOutline />, value: historyCount, label: "History Reports" })}
          {renderStatCard({ tone: "success", icon: <IoBookmarkOutline />, value: totalFocusNotes, label: "Focus Notes" })}
        </div>
        )}

        <div className="home-budget-strip" aria-label="Budget summary and actions">
           {hasOpenScorecard ? (
          <div className="home-budget-tiles">
            {renderStatCard({ tone: "success", icon: <IoTrendingUpOutline />, value: budgetTotals?.underBudget ?? 0, label: "Under Budget" })}
            {renderStatCard({ tone: "error", icon: <IoTrendingDownOutline />, value: budgetTotals?.overBudget ?? 0, label: "Over Budget" })}
          </div>
           ) : (
              <div className="home-budget-tiles home-budget-tiles--overview"></div>
           )}
          <div className="home-action-row">
            {isAdmin && (
              <BestStartButton
                aria-label={isDataRefreshing ? "Refreshing data" : "Re Import data"}
                className="home-action-button"
                color="blue"
                content={<><IoRefresh aria-hidden="true" className={isDataRefreshing ? "spinning" : ""} size={16} /><span>{isDataRefreshing ? "Refreshing..." : "Re Import Data"}</span></>}
                height={38}
                onClick={onTriggerDataRefresh}
                disabled={isDataRefreshing || isLoading || isSaving}
                width={180}
              />
            )}
            {showRefreshButton && (
              <BestStartButton
                aria-label={isLoading ? "Refreshing KPI data" : "Refresh KPI data"}
                className="home-action-button"
                color="blue"
                content={<><IoRefresh aria-hidden="true" className={isLoading ? "spinning" : ""} size={16} /><span>{isLoading ? "Refreshing..." : "Refresh KPI Data"}</span></>}
                height={38}
                onClick={onRefresh}
                disabled={isLoading || isSaving}
                width={180}
              />
            )}
            {showCreateButton && (
            <BestStartButton
              aria-label="Create current month scorecard"
              className="home-action-button"
              color="blue"
              content={<><IoCreateOutline aria-hidden="true" size={16} /><span>Edit Current Month</span></>}
              height={38}
              onClick={onLoadCurrent}
              disabled={isLoading || isSaving}
              width={180}
            />
              )}
            {hasOpenScorecard && (
              <BestStartButton
                aria-label="Scorecard feedback"
                className="home-action-button feedback-button-wrapper"
                color="blue"
                content={ <>
                  {feedbackCount > 0 && <span className="feedback-badge">{feedbackCount}</span>}
                  <IoChatbubbleOutline aria-hidden="true" color="var(--secondary-color)" size={20} />
                  <span>Feedback</span>
                </> }
                height={38}
                onClick={onOpenFeedback}
                disabled={isLoading || isSaving}
                width={180}
              />
            )}
              {showSaveButton && (
              <BestStartButton
                aria-label={isSaving ? "Saving scorecard" : "Save scorecard"}
                className="home-action-button"
                color="green"
                content={<><IoSaveOutline aria-hidden="true" size={16} /><span>{isSaving ? "Saving..." : "Save scorecard"}</span></>}
                height={38}
                onClick={onSave}
                disabled={isLoading || isSaving}
                width={180}
              />
            )}
            {showCancelButton && (
              <BestStartButton
                text="Close"
                aria-label={isViewingHistory ? "Close history view" : "Close current scorecard"}
                className="home-action-button"
                content={<><IoClose  aria-hidden="true" size={16} /><span>{isViewingHistory ? "Close History" : "Close Scorecard"}</span></>}
                color="red"
                height={38}
                onClick={onCancel}
                disabled={isLoading || isSaving || isGoalSaving || historyLoading || previousMonthLoading}
                width={180}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default memo(ScorecardTopbar);
